import { useState } from 'react';
import { Camera, X } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';

const EditModal = ({ title, fields = [], initialValues = {}, onSave, onCancel }) => {
  const [form, setForm] = useState({ ...initialValues });
  const [saving, setSaving] = useState(false);
  const [previews, setPreviews] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'file') return;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleRadioSelect = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const getImageSrc = (fieldName, fallback = '/assets/smartpet-default.jpg') => {
    if (previews[fieldName]) return previews[fieldName];
    const value = form[fieldName];
    if (value && typeof value === 'string') {
      return getImageUrl(value);
    }
    return fallback;
  };

  return (
    <div className="sp-modal-backdrop" onClick={onCancel}>
      {/* Estilos del modal */}
      <style>{`
        .sp-modal-custom {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.15);
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          animation: fadeInUp 0.25s ease;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sp-modal-custom__header {
          padding: 1.5rem 1.75rem 0.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .sp-modal-custom__title {
          font-size: 1.3rem;
          font-weight: 700;
          color: var(--sp-text);
          margin: 0;
        }
        .sp-modal-custom__close {
          background: none;
          border: none;
          cursor: pointer;
          color: #999;
          padding: 4px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sp-modal-custom__close:hover {
          background: #f5f5f5;
          color: #333;
        }
        .sp-modal-custom__body {
          padding: 1rem 1.75rem 0.5rem;
        }
        .sp-modal-custom__actions {
          padding: 1rem 1.75rem 1.5rem;
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          border-top: 1px solid #f0f0f0;
          margin-top: 1rem;
        }
        .sp-field-custom {
          margin-bottom: 1rem;
        }
        .sp-field-custom label {
          display: block;
          margin-bottom: 0.35rem;
          font-weight: 600;
          font-size: 0.9rem;
          color: var(--sp-text);
        }
        .sp-field-custom input,
        .sp-field-custom select,
        .sp-field-custom textarea {
          width: 100%;
          padding: 0.65rem 0.9rem;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 0.95rem;
          transition: border 0.2s;
          outline: none;
          background: #fafafa;
        }
        .sp-field-custom input:focus,
        .sp-field-custom select:focus,
        .sp-field-custom textarea:focus {
          border-color: var(--sp-primary);
          background: #fff;
        }
        .sp-field-custom textarea {
          resize: vertical;
          min-height: 70px;
        }
        .sp-toggle-custom {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0.5rem 0;
        }
        .sp-toggle-custom .sp-switch {
          position: relative;
          width: 44px;
          height: 24px;
        }
        .sp-toggle-custom .sp-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .sp-toggle-custom .sp-switch__slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }
        .sp-toggle-custom .sp-switch__slider:before {
          content: "";
          position: absolute;
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .sp-toggle-custom input:checked + .sp-switch__slider {
          background-color: var(--sp-primary);
        }
        .sp-toggle-custom input:checked + .sp-switch__slider:before {
          transform: translateX(20px);
        }
      `}</style>

      <div className="sp-modal-custom" onClick={(e) => e.stopPropagation()}>
        <div className="sp-modal-custom__header">
          <h3 className="sp-modal-custom__title">{title}</h3>
          <button className="sp-modal-custom__close" onClick={onCancel} type="button">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="sp-modal-custom__body">
            {fields.map((field) => (
              <div className="sp-field-custom" key={field.name}>
                {/* ── Campo de imagen ── */}
                {field.type === 'image' ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '0.5rem 0' }}>
                    <label style={{ position: 'relative', cursor: 'pointer', display: 'inline-block' }}>
                      <img
                        src={getImageSrc(field.name)}
                        alt=""
                        style={{
                          width: field.size || 150,
                          height: field.size || 150,
                          objectFit: 'cover',
                          borderRadius: field.rounded ? '50%' : 14,
                          border: '3px solid #eee',
                          display: 'block',
                          background: '#f9f9f9',
                        }}
                        onError={(e) => {
                          e.currentTarget.src = '/assets/smartpet-default.jpg';
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 6,
                          right: 6,
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          background: 'var(--sp-primary)',
                          color: '#fff',
                          border: '3px solid #fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                        }}
                      >
                        <Camera size={15} />
                      </div>
                      <input
                        type="file"
                        accept={field.accept || 'image/*'}
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setPreviews((prev) => ({
                              ...prev,
                              [field.name]: URL.createObjectURL(file),
                            }));
                          }
                          if (field.onChange) field.onChange(e);
                          setForm((prev) => ({ ...prev, [field.name]: file }));
                        }}
                      />
                    </label>
                  </div>
                ) : field.type === 'checkbox' ? (
                  /* ── Toggle / Checkbox ── */
                  <div className="sp-toggle-custom">
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{field.label}</span>
                    <label className="sp-switch">
                      <input
                        type="checkbox"
                        name={field.name}
                        checked={!!form[field.name]}
                        onChange={handleChange}
                      />
                      <span className="sp-switch__slider"></span>
                    </label>
                  </div>
                ) : field.type === 'radio' ? (
                  /* ── Radio buttons ── */
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600, fontSize: '0.9rem' }}>
                      {field.label}
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {(field.options || []).map((opt) => {
                        const active = String(form[field.name]) === String(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleRadioSelect(field.name, opt.value)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              padding: '0.55rem 1rem',
                              borderRadius: 999,
                              border: active ? '2px solid var(--sp-primary)' : '1px solid #ddd',
                              background: active ? 'var(--sp-primary-soft, #eee7ff)' : '#fff',
                              color: active ? 'var(--sp-primary)' : '#555',
                              fontWeight: 700,
                              cursor: 'pointer',
                              fontSize: '0.88rem',
                              transition: 'all 0.2s',
                            }}
                          >
                            {opt.icon && <span style={{ fontSize: '1.05rem' }}>{opt.icon}</span>}
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : field.type === 'select' ? (
                  /* ── Select ── */
                  <>
                    <label>{field.label}</label>
                    <select
                      name={field.name}
                      value={form[field.name] ?? ''}
                      onChange={handleChange}
                    >
                      <option value="">Seleccionar...</option>
                      {(field.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </>
                ) : field.type === 'textarea' ? (
                  /* ── Textarea ── */
                  <>
                    <label>{field.label}</label>
                    <textarea
                      name={field.name}
                      value={form[field.name] ?? ''}
                      onChange={handleChange}
                      rows={field.rows || 3}
                    />
                  </>
                ) : field.type === 'file' ? (
                  /* ── Campo de archivo genérico ── */
                  <>
                    <label>{field.label}</label>
                    <input
                      type="file"
                      accept={field.accept}
                      onChange={(e) => {
                        if (field.onChange) field.onChange(e);
                        setForm((prev) => ({ ...prev, [field.name]: e.target.files[0] }));
                      }}
                    />
                  </>
                ) : (
                  /* ── Inputs comunes (text, number, date, etc.) ── */
                  <>
                    <label>{field.label}</label>
                    <input
                      type={field.type || 'text'}
                      name={field.name}
                      value={form[field.name] ?? ''}
                      onChange={handleChange}
                      step={field.step}
                      min={field.min}
                      max={field.max}
                      placeholder={field.placeholder || ''}
                    />
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="sp-modal-custom__actions">
            <button
              type="button"
              className="sp-btn sp-btn--light"
              onClick={onCancel}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="sp-btn sp-btn--primary"
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal;